// src/stores/useMedicalRequestStore.ts
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import { PatientDetails } from '../types';
import { useVerifyPatientDetails } from './useVerifyPatientDetails';
import {
  fetchRepeatPrescriptionRequestApi,
  submitRepeatPrescriptionRequestApi,
} from '../services/repeat-prescription-requests.service';
import {
  sendVerificationCodeApi,
  verifyPatientDetailsAndCodeApi,
} from '../services/session.service';
import { PrescriptionMedication } from '../types/repeat-prescription-request';
import { useVerifyNhsLogin } from './useVerifyNhsLogin';

export const useRepeatPrescriptionRequestStore = defineStore('repeat-prescription-request', () => {
  // these blocks can be extracted into a function
  const expiryTime = ref<Date | null>(null); // +1 hour

  const apiLoading = ref<boolean>(false);
  const setApiLoading = () => (apiLoading.value = true);

  const setApiDone = () => (apiLoading.value = false);

  //
  const sessionToken = ref<string | null>(null);
  const transactionId = ref<string | null>(null);
  const isEmergency = ref<boolean>(false);
  const patientDetails = ref<PatientDetails | null>(null);
  const isValidPatient = ref<boolean>(false);
  //

  const prescriptionsList = ref<PrescriptionMedication[]>([]);
  const prescriptionsAcuteList = ref<PrescriptionMedication[]>([]);

  // User has passed 2FA verification
  const isVerified = ref<boolean>(false);
  //NHS login OK
  const isNhsVerified = ref<boolean>(false);

  const selectedPrescriptionsList = ref<string[]>([]);
  const selectedPrescriptionsAcuteList = ref<string[]>([]);

  const isExpired = computed(() => Date.now() > expiryTime?.value?.getTime());

  const isProxy = ref(false);

  function startNew() {
    createTransactionId();
    expiryTime.value = new Date(Date.now() + 60 * 60 * 1000);
    isEmergency.value = false;
    isNhsVerified.value = false;
    isValidPatient.value = false;
    isVerified.value = false;
    patientDetails.value = null;
    prescriptionsList.value = [];
    prescriptionsAcuteList.value = [];
    selectedPrescriptionsList.value = [];
    selectedPrescriptionsAcuteList.value = [];
  }

  function endCurrent() {
    startNew();
    expiryTime.value = null;
    transactionId.value = null;
  }

  function setIsVerified() {
    isVerified.value = true;
  }

  function setIsNhsVerified(value: boolean = true) {
    isNhsVerified.value = value;
  }

  function createTransactionId() {
    transactionId.value = crypto.randomUUID();
  }

  // Preserves the existing state when the same patient is re-submitting their patient details
  function ensureJourneyForPatient(details: PatientDetails) {
    const isSamePatient =
      transactionId.value !== null &&
      !isExpired.value &&
      !isVerified.value &&
      patientDetails.value !== null &&
      patientDetails.value.firstName === details.firstName &&
      patientDetails.value.lastName === details.lastName &&
      patientDetails.value.postcode === details.postcode &&
      patientDetails.value.dateOfBirth === details.dateOfBirth;
    if (!isSamePatient) {
      startNew();
    }
  }

  function setIsValidPatient(value: boolean) {
    isValidPatient.value = value;
  }

  function setIsEmergency(value: boolean) {
    isEmergency.value = value;
  }

  function setPrescriptionsList(value) {
    prescriptionsList.value = value;
  }
  function setPrescriptionsAcuteList(value) {
    prescriptionsAcuteList.value = value;
  }

  function setSelectedPrescriptionsList(value) {
    selectedPrescriptionsList.value = value;
  }

  function setSelectedPrescriptionsAcuteList(value) {
    selectedPrescriptionsAcuteList.value = value;
  }

  async function send2FAVerificationCode() {
    return await sendVerificationCodeApi(patientDetails.value, transactionId.value, true);
  }

  const verifyPatientDetailsRaw = useVerifyPatientDetails({
    patientDetails,
    isProxy: ref(false),
    transactionId,
    setApiLoading,
    setApiDone,
    setIsValidPatient,
    setIsVerified,
    sendVerificationCodeOnSuccess: true,
  });

  const verifyPatientDetails = async (details: PatientDetails) => {
    if (apiLoading.value) return null;
    ensureJourneyForPatient(details);
    return await verifyPatientDetailsRaw(details);
  };

  const verifyPatientAndCode = async (verificationCode) =>
    await verifyPatientDetailsAndCodeApi({
      patientDetails: patientDetails.value,
      transactionId: transactionId.value,
      verificationCode,
      setIsVerified,
    });

  const verifyNhsLogin = async (code: string, csrfToken: string) => {
    const run = useVerifyNhsLogin({
      code,
      csrfToken,
      setApiLoading,
      setApiDone,
      setIsValidPatient,
      setIsNhsVerified,
      setPatientDetails,
    });

    const result = await run();
    if (result.ok) {
      setIsVerified();
    }
    return result;
  };

  async function fetchPrescriptions() {
    setApiLoading();
    try {
      const result = await fetchRepeatPrescriptionRequestApi();

      if (!result.ok) {
        return { ok: false };
      }

      setPrescriptionsList(result.data.repeatPrescriptions);
      setPrescriptionsAcuteList(result.data.acutePrescriptions);
      return { ok: true };
    } catch (e) {
      console.error('fetchRepeatPrescriptionRequestApi error', e);

      return { ok: false };
    } finally {
      setApiDone();
    }
  }

  async function submitAction() {
    setApiLoading();
    try {
      await submitRepeatPrescriptionRequestApi({
        prescriptionIds: [
          ...(selectedPrescriptionsList.value ?? []),
          ...(selectedPrescriptionsAcuteList.value ?? []),
        ],
      });

      endCurrent();
      return { ok: true };
    } catch (e) {
      return { ok: false, errors: e.data?.errors };
    } finally {
      setApiDone();
    }
  }

  function setPatientDetails(details: PatientDetails | null) {
    patientDetails.value = details;
  }

  return {
    apiLoading,
    expiryTime,
    isNhsVerified,
    isProxy,
    isValidPatient,
    isVerified,
    patientDetails,
    prescriptionsList,
    prescriptionsAcuteList,
    selectedPrescriptionsList,
    selectedPrescriptionsAcuteList,
    sessionToken,
    transactionId,

    // getters
    isExpired,

    // actions
    endCurrent,
    ensureJourneyForPatient,
    fetchPrescriptions,
    send2FAVerificationCode,
    setIsEmergency,
    setIsVerified,
    setPatientDetails,
    setSelectedPrescriptionsList,
    setSelectedPrescriptionsAcuteList,
    startNew,
    submitAction,
    verifyPatientDetails,
    verifyPatientAndCode,
    verifyNhsLogin,
  };
});
